import React, { useState, useMemo } from 'react';
import { ShoppingCart, DollarSign, Calendar, TrendingUp, AlertTriangle, CheckCircle, XCircle, Brain, Zap, Target, PiggyBank } from 'lucide-react';
import { useEvents } from '../hooks/useEvents';
import { useCollection } from '../hooks/useCollection';
import { addMonths, addDays, isBefore, differenceInDays, format } from 'date-fns';
import { collection, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';

interface PurchaseDetails {
  name: string;
  totalAmount: number;
  monthlyPayment: number;
  isRecurring: boolean;
  duration: number;
  purchaseDate: Date;
  category: 'subscription' | 'one-time' | 'financed' | 'essential' | 'luxury';
}

interface AffordabilityAnalysis {
  canAfford: boolean;
  score: number;
  recommendation: string;
  recommendedPurchaseDate: Date | null;
  daysUntilNextPayday: number;
  concerns: string[];
  positives: string[];
  impactAnalysis: {
    currentBalance: number;
    balanceAfterPurchase: number;
    balanceAfterPurchaseAndBills: number;
    upcomingBillsTotal: number;
    upcomingIncomeTotal: number;
    lowestBalance30Days: number;
    lowestBalance30DaysDate: Date | null;
    lowestBalance90Days: number;
    lowestBalance90DaysDate: Date | null;
    lowestBalance180Days: number;
    lowestBalance180DaysDate: Date | null;
    monthlyCommitment: number;
    percentOfIncome: number;
    monthlyProjections: Array<{
      month: number;
      lowestBalance: number;
      endBalance: number;
    }>;
  };
  alternatives: string[];
}

export default function CanIAffordIt() {
  const { user } = useAuth();
  const { events, calculateBalanceAtDate } = useEvents();

  // Query for budget buckets
  const bucketsQuery = useMemo(() => {
    if (!user?.uid) return null;
    return query(
      collection(db, 'budgetBuckets'),
      where('userId', '==', user.uid)
    );
  }, [user?.uid]);

  // Query for savings adjustments
  const adjustmentsQuery = useMemo(() => {
    if (!user?.uid) return null;
    return query(
      collection(db, 'savingsAdjustments'),
      where('userId', '==', user.uid)
    );
  }, [user?.uid]);

  const { data: buckets } = useCollection(bucketsQuery);
  const { data: savingsAdjustments } = useCollection(adjustmentsQuery);
  const [purchase, setPurchase] = useState<PurchaseDetails>({
    name: '',
    totalAmount: 0,
    monthlyPayment: 0,
    isRecurring: false,
    duration: 1,
    purchaseDate: new Date(),
    category: 'one-time'
  });
  const [analysis, setAnalysis] = useState<AffordabilityAnalysis | null>(null);
  const [showResults, setShowResults] = useState(false);

  const calculateEmergencyFund = () => {
    if (!events || !savingsAdjustments) return 0;

    // Sum calendar events with category 'savings'
    const calendarTotal = events
      .filter(event => event.category === 'savings' && event.isPaid)
      .reduce((total, event) => {
        if (event.type === 'bill') {
          return total + event.amount; // Adding to savings
        } else if (event.type === 'payday') {
          return total - event.amount; // Withdrawing from savings
        }
        return total;
      }, 0);

    // Sum adjustments
    const adjustmentsTotal = savingsAdjustments.reduce((total, adj: any) => total + adj.amount, 0);

    return calendarTotal + adjustmentsTotal;
  };

  const analyzePurchase = () => {
    const concerns: string[] = [];
    const positives: string[] = [];
    const alternatives: string[] = [];

    const currentBalance = calculateBalanceAtDate(new Date());
    const purchaseAmount = purchase.isRecurring ? purchase.monthlyPayment : purchase.totalAmount;
    const balanceAfterPurchase = currentBalance - purchaseAmount;

    const paydays = events.filter(e => e.type === 'payday');
    const bills = events.filter(e => e.type === 'bill');

    const monthlyIncome = paydays
      .filter(p => {
        const daysDiff = differenceInDays(new Date(), p.date);
        return daysDiff <= 30 && daysDiff >= 0;
      })
      .reduce((sum, p) => sum + p.amount, 0);

    const monthlyExpenses = bills
      .filter(b => {
        const daysDiff = differenceInDays(new Date(), b.date);
        return daysDiff <= 30 && daysDiff >= 0;
      })
      .reduce((sum, b) => sum + b.amount, 0);

    let lowestBalance30Days = Infinity;
    let lowestBalance30DaysDate: Date | null = null;
    let lowestBalance90Days = Infinity;
    let lowestBalance90DaysDate: Date | null = null;
    let lowestBalance180Days = Infinity;
    let lowestBalance180DaysDate: Date | null = null;
    const monthlyProjections: Array<{ month: number; lowestBalance: number; endBalance: number }> = [];

    // Simulate 3 months ahead (90 days) - checking each day
    for (let monthIndex = 0; monthIndex < 3; monthIndex++) {
      let monthLowest = Infinity;
      let monthEnd = 0;

      // Check each day within this month
      const monthStartDay = monthIndex * 30;
      const monthEndDay = (monthIndex + 1) * 30;

      for (let i = monthStartDay; i < monthEndDay; i++) {
        const checkDate = addDays(purchase.purchaseDate, i);

        // Get the projected running balance for this day from the calendar
        let projectedBalance = calculateBalanceAtDate(checkDate);

        // Subtract the purchase if it's on or after the purchase date
        if (purchase.isRecurring) {
          // For recurring purchases, subtract monthly payment for each completed month
          const daysSincePurchase = i;
          if (daysSincePurchase >= 0) {
            const completedMonths = Math.floor(daysSincePurchase / 30) + 1;
            projectedBalance -= purchase.monthlyPayment * completedMonths;
          }
        } else {
          // One-time purchase - subtract once
          projectedBalance -= purchase.totalAmount;
        }

        // Track lowest balance for this specific month
        if (projectedBalance < monthLowest) {
          monthLowest = projectedBalance;
        }

        // Track overall lowest balances
        if (i < 30 && projectedBalance < lowestBalance30Days) {
          lowestBalance30Days = projectedBalance;
          lowestBalance30DaysDate = checkDate;
        }
        if (i < 90 && projectedBalance < lowestBalance90Days) {
          lowestBalance90Days = projectedBalance;
          lowestBalance90DaysDate = checkDate;
        }
        if (i < 180 && projectedBalance < lowestBalance180Days) {
          lowestBalance180Days = projectedBalance;
          lowestBalance180DaysDate = checkDate;
        }

        // Last day of month is the end balance
        if (i === monthEndDay - 1) {
          monthEnd = projectedBalance;
        }
      }

      monthlyProjections.push({
        month: monthIndex + 1,
        lowestBalance: monthLowest === Infinity ? 0 : monthLowest,
        endBalance: monthEnd
      });
    }

    // Set defaults if no values were recorded
    if (lowestBalance30Days === Infinity) lowestBalance30Days = currentBalance;
    if (lowestBalance90Days === Infinity) lowestBalance90Days = currentBalance;
    if (lowestBalance180Days === Infinity) lowestBalance180Days = currentBalance;

    const monthlyCommitment = purchase.isRecurring ? purchase.monthlyPayment : 0;
    const percentOfIncome = monthlyIncome > 0 ? (purchaseAmount / monthlyIncome) * 100 : 0;

    // Find next payday (unpaid only)
    const futurePaydays = events
      .filter(e => e.type === 'payday' && e.date > new Date() && !e.isPaid)
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    const nextPayday = futurePaydays[0];
    const daysUntilNextPayday = nextPayday ? differenceInDays(nextPayday.date, new Date()) : 30;

    // Check for upcoming bills until next payday (or 30 days if no payday)
    const upcomingBills = events.filter(e =>
      (e.type === 'bill' || e.type === 'bucket') &&
      !e.isPaid &&
      e.date > new Date() &&
      differenceInDays(e.date, new Date()) <= daysUntilNextPayday
    );
    const totalUpcomingBills = upcomingBills.reduce((sum, bill) => sum + bill.amount, 0);

    // Only include the NEXT payday (just the one payday)
    const totalUpcomingIncome = nextPayday ? nextPayday.amount : 0;

    let score = 100;

    // Check if purchase plus upcoming bills minus upcoming income would cause issues
    const balanceAfterPurchaseAndBills = balanceAfterPurchase - totalUpcomingBills + totalUpcomingIncome;

    if (balanceAfterPurchase < 0) {
      score -= 40;
      concerns.push('This purchase would overdraw your account immediately');
    }

    if (upcomingBills.length > 0 || totalUpcomingIncome > 0) {
      const timeframeText = nextPayday ? `until your next payday (${daysUntilNextPayday} days)` : `in the next ${daysUntilNextPayday} days`;

      if (balanceAfterPurchaseAndBills < 0) {
        score -= 30;
        concerns.push(`After this purchase, bills ($${totalUpcomingBills.toFixed(2)}), and income ($${totalUpcomingIncome.toFixed(2)}) ${timeframeText}, you would be overdrawn by $${Math.abs(balanceAfterPurchaseAndBills).toFixed(2)}.`);
      } else if (balanceAfterPurchaseAndBills < 100) {
        score -= 15;
        concerns.push(`${timeframeText.charAt(0).toUpperCase() + timeframeText.slice(1)}, you have $${totalUpcomingBills.toFixed(2)} in bills and $${totalUpcomingIncome.toFixed(2)} in income. After this purchase and all transactions, you'll have only $${balanceAfterPurchaseAndBills.toFixed(2)} remaining.`);
      } else {
        if (totalUpcomingIncome > 0 && totalUpcomingBills > 0) {
          positives.push(`${timeframeText.charAt(0).toUpperCase() + timeframeText.slice(1)}, you have $${totalUpcomingIncome.toFixed(2)} in income and $${totalUpcomingBills.toFixed(2)} in bills. After this purchase and all transactions, you'll have $${balanceAfterPurchaseAndBills.toFixed(2)}.`);
        } else if (totalUpcomingIncome > 0) {
          positives.push(`You have $${totalUpcomingIncome.toFixed(2)} in income ${timeframeText}. After this purchase, you'll have $${balanceAfterPurchaseAndBills.toFixed(2)}.`);
        } else if (totalUpcomingBills > 0) {
          positives.push(`After this purchase and your bills ($${totalUpcomingBills.toFixed(2)}) ${timeframeText}, you'll have $${balanceAfterPurchaseAndBills.toFixed(2)}.`);
        }
      }
    }

    if (lowestBalance30Days < 0) {
      score -= 40;
      concerns.push(`CRITICAL: Your balance will drop to $${lowestBalance30Days.toFixed(2)} (negative) within the next 30 days after this purchase`);
    } else if (lowestBalance30Days < 100) {
      score -= 15;
      concerns.push(`Your lowest balance in the next 30 days will be $${lowestBalance30Days.toFixed(2)}, which is very tight`);
    } else if (lowestBalance30Days < 500) {
      positives.push(`Your lowest balance in the next 30 days will be $${lowestBalance30Days.toFixed(2)}`);
    }

    if (lowestBalance90Days < 0) {
      score -= 25;
      concerns.push(`WARNING: Your balance will drop to $${lowestBalance90Days.toFixed(2)} (negative) within the next 90 days`);
    } else if (lowestBalance90Days < 200) {
      score -= 10;
      concerns.push(`Your lowest balance in the next 90 days will be $${lowestBalance90Days.toFixed(2)}, which is concerning`);
    }

    // Check for negative balances in any month
    const monthsWithNegativeBalance = monthlyProjections.filter(m => m.lowestBalance < 0).length;
    if (monthsWithNegativeBalance > 0) {
      concerns.push(`You will have negative balances in ${monthsWithNegativeBalance} out of the next 3 months`);
    }

    // Check for declining balance trend
    if (monthlyProjections.length >= 3) {
      const firstMonthEnd = monthlyProjections[0].endBalance;
      const lastMonthEnd = monthlyProjections[monthlyProjections.length - 1].endBalance;
      if (lastMonthEnd < firstMonthEnd - 500) {
        concerns.push('Your balance shows a declining trend over the next 6 months');
        score -= 10;
      }
    }

    if (percentOfIncome > 30) {
      score -= 20;
      concerns.push(`This represents ${percentOfIncome.toFixed(1)}% of your monthly income, which is quite high`);
    } else if (percentOfIncome > 15) {
      score -= 10;
      concerns.push(`This represents ${percentOfIncome.toFixed(1)}% of your monthly income`);
    }

    if (purchase.category === 'luxury' && (currentBalance - monthlyExpenses) < purchase.totalAmount * 2) {
      score -= 15;
      concerns.push('Limited buffer for luxury purchases given current financial situation');
    }

    // Check emergency fund (target is $1,000)
    const emergencyFundBalance = calculateEmergencyFund();
    const emergencyFundTarget = 1000;

    if (purchase.category !== 'essential') {
      if (emergencyFundBalance < emergencyFundTarget) {
        const shortfall = emergencyFundTarget - emergencyFundBalance;
        score -= 10;
        concerns.push(`Your emergency fund is $${emergencyFundBalance.toFixed(2)}, which is $${shortfall.toFixed(2)} short of the $${emergencyFundTarget.toFixed(2)} target`);
        alternatives.push(`Build your emergency fund to $${emergencyFundTarget.toFixed(2)} before non-essential purchases`);
      } else {
        positives.push(`You have a solid emergency fund of $${emergencyFundBalance.toFixed(2)} (target: $${emergencyFundTarget.toFixed(2)})`);
        score += 5;
      }
    }

    if (purchase.isRecurring) {
      const subscriptionCount = bills.filter(b => b.isRecurring).length;
      if (subscriptionCount > 5) {
        score -= 5;
        concerns.push(`You already have ${subscriptionCount} recurring bills/subscriptions`);
        alternatives.push('Review existing subscriptions - consider canceling unused ones first');
      }
    }

    if (currentBalance > monthlyExpenses * 3) {
      positives.push('You have a healthy cash buffer');
      score += 5;
    }

    if (purchase.category === 'essential') {
      positives.push('This is categorized as an essential purchase');
      score += 10;
    }

    if (percentOfIncome < 10) {
      positives.push('This purchase is a reasonable percentage of your income');
      score += 5;
    }

    if (balanceAfterPurchase > monthlyExpenses) {
      positives.push('You will still have more than one month of expenses after this purchase');
    }

    if (!purchase.isRecurring) {
      positives.push('One-time purchase - no ongoing commitment');
    }

    if (purchase.isRecurring && purchase.monthlyPayment < monthlyIncome * 0.05) {
      positives.push('Monthly payment is a small percentage of your income');
    }

    if (purchase.totalAmount > currentBalance * 0.5 && purchase.category !== 'essential') {
      alternatives.push('Save up for this purchase over the next few months');

      // Calculate realistic saving timeframe based on purchase amount and income
      const maxReasonableMonthly = monthlyIncome * 0.2; // Don't suggest saving more than 20% of income per month
      const monthsNeeded = Math.ceil(purchase.totalAmount / maxReasonableMonthly);
      const clampedMonths = Math.max(3, Math.min(monthsNeeded, 24)); // Between 3-24 months
      const monthlySavings = purchase.totalAmount / clampedMonths;

      if (monthlySavings > 0 && clampedMonths <= 24) {
        alternatives.push(`Set aside $${monthlySavings.toFixed(2)} per month for ${clampedMonths} months`);
      } else {
        alternatives.push('Consider if this purchase is realistic given your current income level');
      }
    }

    if (purchase.isRecurring) {
      alternatives.push('Look for annual payment options that often offer discounts');
      alternatives.push('Consider trial periods or free alternatives first');
    }

    if (score < 50 && purchase.category === 'luxury') {
      alternatives.push('Delay this purchase until your financial situation improves');
    }

    score = Math.max(0, Math.min(100, score));

    // Find recommended purchase date if not affordable now
    let recommendedPurchaseDate: Date | null = null;
    if (score < 60) {
      // Look ahead 3 months to find when purchase becomes affordable
      for (let i = 1; i < 90; i++) {
        const checkDate = addDays(new Date(), i);

        // Get the projected balance from the calendar for this date
        let projectedBalance = calculateBalanceAtDate(checkDate);

        // Subtract the purchase
        projectedBalance -= purchaseAmount;

        // Check if balance would be healthy (at least $200 buffer)
        if (projectedBalance >= 200) {
          // Also check that next 7 days after purchase would be safe
          let safeForNextWeek = true;
          for (let j = 1; j <= 7; j++) {
            const futureCheckDate = addDays(checkDate, j);
            let futureBalance = calculateBalanceAtDate(futureCheckDate) - purchaseAmount;

            if (futureBalance < 100) {
              safeForNextWeek = false;
              break;
            }
          }

          if (safeForNextWeek) {
            recommendedPurchaseDate = checkDate;
            break;
          }
        }
      }
    }

    let recommendation = '';
    let canAfford = false;

    if (score >= 80) {
      recommendation = 'Go for it! Your finances can comfortably handle this purchase. You have good financial stability and this purchase fits well within your budget.';
      canAfford = true;
    } else if (score >= 60) {
      recommendation = 'This purchase is feasible, but exercise caution. Make sure you have a plan and monitor your spending closely over the next few months.';
      canAfford = true;
    } else if (score >= 40) {
      if (recommendedPurchaseDate) {
        recommendation = `Think carefully before proceeding. This purchase could strain your finances right now. Consider waiting until ${format(recommendedPurchaseDate, 'MMMM d, yyyy')} when your financial situation will be more stable.`;
      } else {
        recommendation = 'Think carefully before proceeding. While technically possible, this purchase could strain your finances. Consider the alternatives suggested below.';
      }
      canAfford = false;
    } else {
      if (recommendedPurchaseDate) {
        recommendation = `Not recommended right now. This purchase poses significant financial risk at this time. I recommend waiting until ${format(recommendedPurchaseDate, 'MMMM d, yyyy')} when you'll be better positioned financially.`;
      } else {
        recommendation = 'Not recommended. This purchase poses significant financial risk. I strongly advise against it at this time. Focus on building financial stability first.';
      }
      canAfford = false;
    }

    const result: AffordabilityAnalysis = {
      canAfford,
      score,
      recommendation,
      recommendedPurchaseDate,
      daysUntilNextPayday,
      concerns,
      positives,
      impactAnalysis: {
        currentBalance,
        balanceAfterPurchase,
        balanceAfterPurchaseAndBills,
        upcomingBillsTotal: totalUpcomingBills,
        upcomingIncomeTotal: totalUpcomingIncome,
        lowestBalance30Days,
        lowestBalance30DaysDate,
        lowestBalance90Days,
        lowestBalance90DaysDate,
        lowestBalance180Days,
        lowestBalance180DaysDate,
        monthlyCommitment,
        percentOfIncome,
        monthlyProjections
      },
      alternatives
    };

    setAnalysis(result);
    setShowResults(true);
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'from-green-500 to-emerald-500';
    if (score >= 60) return 'from-yellow-500 to-orange-500';
    if (score >= 40) return 'from-orange-500 to-red-500';
    return 'from-red-600 to-red-800';
  };

  const getScoreIcon = (score: number) => {
    if (score >= 80) return <CheckCircle className="w-16 h-16" />;
    if (score >= 60) return <AlertTriangle className="w-16 h-16" />;
    return <XCircle className="w-16 h-16" />;
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent flex items-center justify-center mb-4">
          <Brain className="w-10 h-10 mr-3 text-cyan-600" />
          Can I Afford It?
        </h1>
        <p className="text-lg text-slate-600">
          AI-powered financial analysis to help you make smart purchase decisions
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass-effect rounded-2xl p-6 cyber-glow">
          <div className="flex items-center mb-6">
            <ShoppingCart className="w-6 h-6 mr-3 text-blue-600" />
            <h2 className="text-2xl font-bold text-slate-800">Purchase Details</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                What do you want to buy?
              </label>
              <input
                type="text"
                value={purchase.name}
                onChange={(e) => setPurchase({ ...purchase, name: e.target.value })}
                placeholder="e.g., New Laptop, Netflix Subscription, Car Payment"
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Category
              </label>
              <select
                value={purchase.category}
                onChange={(e) => setPurchase({ ...purchase, category: e.target.value as any })}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
              >
                <option value="one-time">One-Time Purchase</option>
                <option value="subscription">Subscription</option>
                <option value="financed">Financed Purchase</option>
                <option value="essential">Essential</option>
                <option value="luxury">Luxury/Want</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Total Amount
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type="number"
                    value={purchase.totalAmount || ''}
                    onChange={(e) => setPurchase({ ...purchase, totalAmount: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                    className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Purchase Date
                </label>
                <input
                  type="date"
                  value={purchase.purchaseDate.toISOString().split('T')[0]}
                  onChange={(e) => setPurchase({ ...purchase, purchaseDate: new Date(e.target.value) })}
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="recurring"
                checked={purchase.isRecurring}
                onChange={(e) => setPurchase({ ...purchase, isRecurring: e.target.checked })}
                className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <label htmlFor="recurring" className="text-sm font-medium text-slate-700">
                This is a recurring payment (subscription, monthly bill, etc.)
              </label>
            </div>

            {purchase.isRecurring && (
              <div className="grid grid-cols-2 gap-4 pl-7">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Monthly Payment
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                    <input
                      type="number"
                      value={purchase.monthlyPayment || ''}
                      onChange={(e) => setPurchase({ ...purchase, monthlyPayment: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                      className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Duration (months)
                  </label>
                  <input
                    type="number"
                    value={purchase.duration || ''}
                    onChange={(e) => setPurchase({ ...purchase, duration: parseInt(e.target.value) || 1 })}
                    placeholder="12"
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                  />
                </div>
              </div>
            )}

            <button
              onClick={analyzePurchase}
              disabled={!purchase.name || (!purchase.totalAmount && !purchase.monthlyPayment)}
              className="w-full py-4 gradient-primary text-white rounded-xl font-semibold text-lg hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              <Zap className="w-5 h-5" />
              <span>Analyze Purchase</span>
            </button>
          </div>
        </div>

        {showResults && analysis && (
          <div className="glass-effect rounded-2xl p-6 cyber-glow animate-fadeIn">
            <div className="text-center mb-6">
              <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-r ${getScoreColor(analysis.score)} text-white mb-4 shadow-lg`}>
                {getScoreIcon(analysis.score)}
              </div>
              <h2 className="text-3xl font-bold text-slate-800 mb-2">
                Affordability Score: {analysis.score}/100
              </h2>
              <p className="text-lg text-slate-600">{analysis.recommendation}</p>

              {analysis.recommendedPurchaseDate && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-center space-x-2">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-blue-900">
                      Recommended Purchase Date: {format(analysis.recommendedPurchaseDate, 'MMMM d, yyyy')}
                    </span>
                  </div>
                  <p className="text-sm text-blue-700 mt-2">
                    Based on your upcoming income and bills, this is when you'll be financially ready for this purchase.
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center">
                  <Target className="w-5 h-5 mr-2 text-blue-600" />
                  Financial Impact
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between p-2 rounded bg-slate-50">
                    <span className="text-slate-600">Current Balance:</span>
                    <span className="font-semibold text-slate-800">${analysis.impactAnalysis.currentBalance.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between p-2 rounded bg-slate-50">
                    <span className="text-slate-600">After Purchase:</span>
                    <span className={`font-semibold ${analysis.impactAnalysis.balanceAfterPurchase < 0 ? 'text-red-600' : 'text-slate-800'}`}>
                      ${analysis.impactAnalysis.balanceAfterPurchase.toFixed(2)}
                    </span>
                  </div>
                  {(analysis.impactAnalysis.upcomingBillsTotal > 0 || analysis.impactAnalysis.upcomingIncomeTotal > 0) && (
                    <>
                      {analysis.impactAnalysis.upcomingIncomeTotal > 0 && (
                        <div className="flex justify-between p-2 rounded bg-green-50 border border-green-200">
                          <span className="text-green-800 font-medium">Upcoming Income (until next payday):</span>
                          <span className="font-semibold text-green-900">+${analysis.impactAnalysis.upcomingIncomeTotal.toFixed(2)}</span>
                        </div>
                      )}
                      {analysis.impactAnalysis.upcomingBillsTotal > 0 && (
                        <div className="flex justify-between p-2 rounded bg-amber-50 border border-amber-200">
                          <span className="text-amber-800 font-medium">Upcoming Bills (until next payday):</span>
                          <span className="font-semibold text-amber-900">-${analysis.impactAnalysis.upcomingBillsTotal.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between p-2 rounded bg-blue-50 border border-blue-200">
                        <span className="text-blue-800 font-medium">Net After All Transactions:</span>
                        <span className={`font-semibold ${analysis.impactAnalysis.balanceAfterPurchaseAndBills < 0 ? 'text-red-600' : analysis.impactAnalysis.balanceAfterPurchaseAndBills < 200 ? 'text-orange-600' : 'text-green-600'}`}>
                          ${analysis.impactAnalysis.balanceAfterPurchaseAndBills.toFixed(2)}
                        </span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between p-2 rounded bg-slate-50">
                    <span className="text-slate-600">
                      Lowest Balance (30 days):
                      {analysis.impactAnalysis.lowestBalance30DaysDate && (
                        <span className="block text-xs text-slate-500 mt-0.5">
                          on {format(analysis.impactAnalysis.lowestBalance30DaysDate, 'MMM d, yyyy')}
                        </span>
                      )}
                    </span>
                    <span className={`font-semibold ${analysis.impactAnalysis.lowestBalance30Days < 0 ? 'text-red-600' : 'text-slate-800'}`}>
                      ${analysis.impactAnalysis.lowestBalance30Days.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between p-2 rounded bg-slate-50">
                    <span className="text-slate-600">
                      Lowest Balance (90 days):
                      {analysis.impactAnalysis.lowestBalance90DaysDate && (
                        <span className="block text-xs text-slate-500 mt-0.5">
                          on {format(analysis.impactAnalysis.lowestBalance90DaysDate, 'MMM d, yyyy')}
                        </span>
                      )}
                    </span>
                    <span className={`font-semibold ${analysis.impactAnalysis.lowestBalance90Days < 0 ? 'text-red-600' : 'text-slate-800'}`}>
                      ${analysis.impactAnalysis.lowestBalance90Days.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between p-2 rounded bg-slate-50">
                    <span className="text-slate-600">
                      Lowest Balance (3 months):
                      {analysis.impactAnalysis.lowestBalance180DaysDate && (
                        <span className="block text-xs text-slate-500 mt-0.5">
                          on {format(analysis.impactAnalysis.lowestBalance180DaysDate, 'MMM d, yyyy')}
                        </span>
                      )}
                    </span>
                    <span className={`font-semibold ${analysis.impactAnalysis.lowestBalance180Days < 0 ? 'text-red-600' : 'text-slate-800'}`}>
                      ${analysis.impactAnalysis.lowestBalance180Days.toFixed(2)}
                    </span>
                  </div>
                  {analysis.impactAnalysis.monthlyCommitment > 0 && (
                    <div className="flex justify-between p-2 rounded bg-slate-50">
                      <span className="text-slate-600">New Monthly Commitment:</span>
                      <span className="font-semibold text-slate-800">${analysis.impactAnalysis.monthlyCommitment.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between p-2 rounded bg-slate-50">
                    <span className="text-slate-600">% of Monthly Income:</span>
                    <span className="font-semibold text-slate-800">{analysis.impactAnalysis.percentOfIncome.toFixed(1)}%</span>
                  </div>
                </div>
              </div>

              {analysis.positives.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                    Positives
                  </h3>
                  <ul className="space-y-2">
                    {analysis.positives.map((positive, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <span className="text-green-600 mr-2">✓</span>
                        <span className="text-slate-700">{positive}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {analysis.concerns.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center">
                    <AlertTriangle className="w-5 h-5 mr-2 text-orange-600" />
                    Concerns
                  </h3>
                  <ul className="space-y-2">
                    {analysis.concerns.map((concern, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <span className="text-orange-600 mr-2">⚠</span>
                        <span className="text-slate-700">{concern}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center">
                  <Calendar className="w-5 h-5 mr-2 text-blue-600" />
                  6-Month Outlook
                </h3>
                <div className="space-y-2">
                  {analysis.impactAnalysis.monthlyProjections.map((projection) => (
                    <div key={projection.month} className="flex items-center justify-between p-2 rounded bg-slate-50 text-sm">
                      <span className="text-slate-600 font-medium">Month {projection.month}:</span>
                      <div className="flex space-x-4">
                        <div>
                          <span className="text-slate-500 text-xs">Lowest: </span>
                          <span className={`font-semibold ${projection.lowestBalance < 0 ? 'text-red-600' : projection.lowestBalance < 200 ? 'text-orange-600' : 'text-green-600'}`}>
                            ${projection.lowestBalance.toFixed(2)}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-500 text-xs">End: </span>
                          <span className={`font-semibold ${projection.endBalance < 0 ? 'text-red-600' : projection.endBalance < 200 ? 'text-orange-600' : 'text-slate-800'}`}>
                            ${projection.endBalance.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {analysis.alternatives.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center">
                    <PiggyBank className="w-5 h-5 mr-2 text-cyan-600" />
                    Alternatives & Tips
                  </h3>
                  <ul className="space-y-2">
                    {analysis.alternatives.map((alt, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <span className="text-cyan-600 mr-2">💡</span>
                        <span className="text-slate-700">{alt}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {!showResults && (
          <div className="glass-effect rounded-2xl p-6 flex items-center justify-center">
            <div className="text-center text-slate-400">
              <Brain className="w-24 h-24 mx-auto mb-4 opacity-50" />
              <p className="text-lg">Fill in the purchase details to get your AI-powered analysis</p>
            </div>
          </div>
        )}
      </div>

      <div className="mt-8 glass-effect rounded-xl p-6">
        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center">
          <TrendingUp className="w-5 h-5 mr-2 text-blue-600" />
          How It Works
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-slate-700">
          <div>
            <div className="font-semibold text-blue-600 mb-2">1. Analyzes Until Next Payday</div>
            <p>Reviews your current balance and all income and bills between now and your next payday</p>
          </div>
          <div>
            <div className="font-semibold text-cyan-600 mb-2">2. Projects 6 Months Ahead</div>
            <p>Simulates your finances for 6 months to ensure long-term stability after this purchase</p>
          </div>
          <div>
            <div className="font-semibold text-green-600 mb-2">3. Recommends Best Purchase Date</div>
            <p>If not affordable now, suggests the optimal date within 3 months when you'll be ready</p>
          </div>
        </div>
      </div>
    </div>
  );
}
